import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from 'react-router-dom';
import Layout from '../Layout';
import SkeletonPage from '../modules/skeleton/SkeletonPage';
import AdminRoutes from './AdminRoutes';
import CompanyDetailsRoutes from './CompanyDetailsRoutes';
import InternalPurchaseRoutes from './InternalPurchaseRoutes';
import MaterialsIndentRoutes from './MaterialsIndentRoutes';
import MessageRoutes from './MessageRoutes';
import PoBillsRoutes from './PoBillsRoutes';
import ProjectRoutes from './ProjectRoutes';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import PurchaseOrderRoutes from './PurchaseOrderRoutes';
import SettingRoutes from './SettingRoutes';
import StocksRoutes from './StocksRoutes';
import TaskRoutes from './TaskRoutes';
import VendorsRoutes from './VendorsRoutes';
import AttendanceRoutes from './AttendanceRoutes';
import WarehouseRoutes from './WarehouseRoutes';
import ReqProjectTypeRoutes from './ReqProjectTypeRoutes';
import FinanceOverviewPage from '../modules/accounts';
import PendingActions from '../modules/ready-for-actions';
import { RBACRoute } from './RBACRoute';
import PRIVILEGES from '../constants/privileges';
// import AnalyticsDashboard from '../modules/dashboard/AnalyticsDashboard';

const AccountsQuotations = lazy(() => import('../modules/accounts/pages/Quotations'));
const AccountsQuotationDetails = lazy(() => import('../modules/accounts/pages/QuotationDetails'));
const AccountsClientTypes = lazy(() => import('../modules/accounts/pages/ClientTypes'));
const AccountsDefineColumns = lazy(() => import('../modules/accounts/pages/DefineColumns'));
const AccountsStatusManagement = lazy(() => import('../modules/accounts/pages/StatusManagement'));
const AccountsLineItemSearch = lazy(() => import('../modules/accounts/pages/LineItemSearch'));

const ErrorPage = lazy(() => import('../modules/error/ErrorPage'));
const Projects = lazy(() => import('../modules/projects'));
const Login = lazy(() => import('../modules/login'));
const Signup = lazy(() => import('../modules/signup'));
const Dashboard = lazy(() => import('../modules/dashboard'));
const Profile = lazy(() => import('../modules/profile'));
const Reports = lazy(() => import('../modules/reports'));
const ClientPricing = lazy(() => import('../modules/client-pricing'));
const Machinery = lazy(() => import('../modules/machinery'));
const UnauthorizedPage = lazy(() => import('../modules/unauthorized'));

// Notification Pages
const NotificationPage = lazy(() => import('../modules/notification'));
const NotificationSettingsPage = lazy(() => import('../modules/notification/pages/NotificationSettingsPage'));

// Vendor Public Pages
const VendorPublicPage = lazy(() => import('../modules/purchase-enquiry/pages/VendorPublicPage'));

// CRM Pages
const CRMLeads = lazy(() => import('../modules/CRM/leads'));
const CRMActivities = lazy(() => import('../modules/CRM/Activities'));
const CRMFollowups = lazy(() => import('../modules/CRM/Followups'));
const CRMSettings = lazy(() => import('../modules/CRM/settings'));
const CRMLeadDetails = lazy(() => import('../modules/CRM/leads/Details'));

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        path="login"
        element={
          <PublicRoute>
            <Suspense fallback={<SkeletonPage />}>
              <Login />
            </Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="signup"
        element={
          <PublicRoute>
            <Suspense fallback={<SkeletonPage />}>
              <Signup />
            </Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="unauthorized"
        element={
          <Suspense fallback={<SkeletonPage />}>
            <UnauthorizedPage />
          </Suspense>
        }
      />
      <Route
        path="/purchases/po-enquiry/public/vendor/:token?"
        element={
          <Suspense fallback={<SkeletonPage />}>
            <VendorPublicPage />
          </Suspense>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
        errorElement={<ErrorPage />}
      >
        <Route
          index
          element={
            <Suspense fallback={<SkeletonPage />}>
              <Projects />
            </Suspense>
          }
        />

        <Route
          path="/dashboard"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <Dashboard />
              {/* <AnalyticsDashboard /> */}
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/profile"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <Profile />
              {/* <AnalyticsDashboard /> */}
              {/* <Dashboard /> */}
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/reports"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <Reports />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="project/:pID/client-pricing"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <ClientPricing />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/field-items"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <Machinery />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/accounts"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <FinanceOverviewPage />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/accounts/quotations"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <AccountsQuotations />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>

        <Route
          path="/accounts/quotations/details/:id"
          element={
            <RBACRoute privileges={[PRIVILEGES.COMMON.QUOTATION_VIEW_DETAILS]}>  
            <Suspense fallback={<SkeletonPage />}>
              <AccountsQuotationDetails />
            </Suspense>
            </RBACRoute>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/accounts/client-types"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <AccountsClientTypes />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/accounts/define-columns"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <AccountsDefineColumns />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/accounts/status-management"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <AccountsStatusManagement />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/accounts/quotations/details/:id/line-item-search"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <AccountsLineItemSearch />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>
        <Route
          path="/pending-actions"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <PendingActions />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        ></Route>

        {/* Notification Routes */}
        <Route
          path="/notifications"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <NotificationPage />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        />
        <Route
          path="/notification-settings"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <NotificationSettingsPage />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        />

        {/* CRM Routes */}
        <Route
          path="/crm/leads"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CRMLeads />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        />
        {/* <Route
          path="/crm/clients"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CRMClients />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        /> */}
        <Route
          path="/crm/activities"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CRMActivities />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        />
        <Route
          path="/crm/followups"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CRMFollowups />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        />
        <Route
          path="/crm/settings"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CRMSettings />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        />
        <Route
          path="/crm/lead/:id/details"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CRMLeadDetails />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        />
        {/* <Route
          path="/crm/client/:id/details"
          element={
            <Suspense fallback={<SkeletonPage />}>
              <CRMClientDetails />
            </Suspense>
          }
          errorElement={<ErrorPage />}
        /> */}

        {ReqProjectTypeRoutes}
        {ProjectRoutes}
        {TaskRoutes}
        {MessageRoutes}
        {SettingRoutes}
        {AdminRoutes}
        {WarehouseRoutes}
        {MaterialsIndentRoutes}
        {StocksRoutes}
        {PurchaseOrderRoutes}
        {InternalPurchaseRoutes}
        {VendorsRoutes}
        {PoBillsRoutes}
        {AttendanceRoutes}
        {CompanyDetailsRoutes}
        <Route path="*" element={<ErrorPage />} />
      </Route>
    </>
  )
);

export default router;
